import React, { useState } from "react";
import { Group } from "@vx/group";
import { scaleTime, scaleLinear, scaleOrdinal, scalePoint } from "@vx/scale";
import styled, { withTheme } from "styled-components";
import { withTooltip, Tooltip } from "@vx/tooltip";
import { AxisBottom } from "@vx/axis";
import { LinePath, Circle } from "@vx/shape";
import { curveLinear } from "@vx/curve";
import { LegendItem, LegendOrdinal, LegendLabel } from "@vx/legend";
import { GridRows } from "@vx/grid";
import { ParentSize } from "@vx/responsive";
import moment from "moment";
import { localPoint } from "@vx/event";
import { useTranslation } from "react-i18next";
import {
  decimalPlaces,
  roundMealPercentage,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { getDateFormatWithoutYear, UNIT } from "../../../modules/constants";
import { isRtl } from "../../../i18n/helpers";

const LineChartWrapper = styled.div`
  height: 300px;
  display: flex;
  flex-direction: row;
`;
const StyledLegendLabel = styled(LegendLabel)`
  user-select: none;
  margin: 0 5px !important;
`;

const FixedPercentageAxis = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  margin-bottom: 54px;
  font-size: 11px;
`;

const StyledWrapper = styled.div`
  height: 310px;
  overflow-x: ${(props) => (props.$hideScrollbar ? "hidden" : "scroll")};
  overflow-y: hidden;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  .description {
    padding-bottom: 10px;
    color: ${(props) => props.theme?.nero};
    font-size: 14px;
    user-select: none;
  }
  .legend {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    @media (max-width: 1100px) {
      flex-direction: column;
    }
  }
`;

const LabelsComponent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  @media (max-width: 647px) {
    flex-direction: column;
  }
`;

const LegendInfo = styled.div`
  user-select: none;
  display: flex;
  flex-direction: row;
  @media (max-width: 647px) {
    flex-direction: column;
  }
`;

const StyledCircle = styled.svg`
  margin-right: 5px;
  margin-bottom: 4px;
`;

const StudyLineChart = (props) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
    hideScrollbar,
  } = props;

  const { t } = useTranslation();

  const [showProteinLine, setShowProteinLine] = useState(true);
  const [showMediumChainFatsLine, setShowMediumChainFatsLine] = useState(true);
  const [showLongChainFatsLine, setShowLongChainFatsLine] = useState(true);
  const [showCarbohydratesLine, setShowCarbohydratesLine] = useState(true);
  const [showCaloriesLabels, setShowCaloriesLabels] = useState(true);
  const [legendList, setLegendList] = useState([
    "lcf",
    "mcf",
    "protein",
    "carbohydrates",
    "calories",
  ]);
  const data = props.entries;
  const strokeWidth = 4;
  const yAxisValue = ["0", "20", "40", "60", "80", "100"];
  const ordinalColorScale = scaleOrdinal({
    domain: ["lcf", "mcf", "protein", "carbohydrates", "calories"],
    range: [
      props.theme?.green,
      props.theme?.darkGreen,
      props.theme?.blueMagenta,
      props.theme?.magentaPink,
      props.theme?.geminiGray,
    ],
  });
  const tooltipStyles = {
    minWidth: 60,
    backgroundColor: "rgba(0,0,0,0.9)",
    color: props.theme?.white,
    userSelect: "none",
  };

  const handleCheckMark = (line) => {
    if (legendList.includes(line)) {
      setLegendList(legendList.filter((e) => e !== line));
    } else {
      setLegendList((legendList) => [...legendList, line]);
    }
  };

  const handleClickLegend = (line) => {
    handleCheckMark(line.datum);
    switch (line.datum) {
      case "lcf":
        setShowLongChainFatsLine((prevState) => !prevState);
        break;
      case "mcf":
        setShowMediumChainFatsLine((prevState) => !prevState);
        break;
      case "protein":
        setShowProteinLine((prevState) => !prevState);
        break;
      case "carbohydrates":
        setShowCarbohydratesLine((prevState) => !prevState);
        break;
      case "calories":
        setShowCaloriesLabels((prevState) => !prevState);
        break;
      default:
        break;
    }
  };

  const getLegendName = (label) => {
    if (isRtl()) {
      if (label.datum === "lcf") {
        return t("appText.dietDiary.longChainFats");
      }

      if (label.datum === "mcf") {
        return t("appText.dietDiary.mediumChainFats");
      }
    }

    return t(`appText.dietDiary.${label.datum}`);
  };

  const handleShowTooltip = (e, data, nMeals) => {
    const coords = localPoint(e.target.ownerSVGElement, e);
    const circleInfo = e.target.getBoundingClientRect();
    const leftAxis = document.getElementById("y-axis").getBoundingClientRect();
    showTooltip({
      tooltipData: { data, nMeals },
      tooltipLeft: isRtl()
        ? circleInfo.left - 30
        : circleInfo.left - leftAxis.left + 20,
      tooltipTop: coords.y - 20,
    });
  };

  return (
    <>
      <ParentSize data-testid="study-line-chart">
        {({ width, height }) => {
          const x = (d) => moment(d.dateDefaultLocale);
          const protein = (d) => d.protein.percentage || 0;
          const carbohydrates = (d) => d.carbohydrates.percentage || 0;
          const mediumChainFats = (d) => d.mediumChainFats.percentage || 0;
          const longChainFats = (d) => d.longChainFats.percentage || 0;
          const calories = data.map((a) =>
            Number(roundMealValue(a.calories.value))
              .toFixed(decimalPlaces)
              .toString()
          );
          const scrollableWidth = data.length * 100;
          const chartWidth =
            scrollableWidth < width
              ? width - (isRtl() ? 30 : 0)
              : scrollableWidth - (isRtl() ? 50 : 0);
          const xMax = isRtl() ? chartWidth - 60 : chartWidth - 120;
          const yMax = height - 60;
          const yScale = scaleLinear({
            rangeRound: [10, yMax],
            domain: [100, 0],
          });
          const xScale = scaleTime({
            rangeRound: [0, xMax],
            tickValues: data.map((d) => d.date),
            domain: isRtl()
              ? [Math.max(...data.map(x)), Math.min(...data.map(x))]
              : [Math.min(...data.map(x)), Math.max(...data.map(x))],
          });
          const caloriesScale = scalePoint({
            range: isRtl() ? [xMax, 0] : [0, xMax],
            domain: calories.map((name, index) => 1000 + index + name),
          });
          return (
            <>
              <LineChartWrapper>
                <FixedPercentageAxis id="y-axis">
                  {yAxisValue.map((val, i) => (
                    <span key={`y${i}`}>{`${val}${UNIT.PERCENTAGE}`}</span>
                  ))}
                </FixedPercentageAxis>
                <StyledWrapper $hideScrollbar={hideScrollbar}>
                  <svg width={chartWidth} height={height}>
                    <Group key={"ds"} left={30}>
                      <GridRows
                        scale={yScale}
                        width={chartWidth}
                        pointerEvents="none"
                        numTicks={4}
                      />
                      {showCaloriesLabels && (
                        <AxisBottom
                          tickLabelProps={() => ({
                            fill: props.theme?.geminiGray,
                            fontSize: 11,
                            textAnchor: "middle",
                          })}
                          hideAxisLine={true}
                          hideTicks={true}
                          tickFormat={(d) => d.substring(4) + "kcal"}
                          scale={caloriesScale}
                          labelOffset={15}
                          top={yMax + 5}
                        />
                      )}
                      <AxisBottom
                        tickLabelProps={() => ({
                          fontSize: 11,
                          fontWeight: 600,
                          textAnchor: "middle",
                        })}
                        hideAxisLine={true}
                        hideTicks={true}
                        tickFormat={(d) =>
                          moment(d).format(getDateFormatWithoutYear())
                        }
                        scale={xScale}
                        numTicks={
                          data.length <= 3 ? data.length - 1 : data.length
                        }
                        labelOffset={15}
                        top={yMax + 25}
                      />
                      {showProteinLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(protein(d))}
                          stroke={props.theme?.blueMagenta}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showCarbohydratesLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(carbohydrates(d))}
                          stroke={props.theme?.magentaPink}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showLongChainFatsLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(longChainFats(d))}
                          stroke={props.theme?.green}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {showMediumChainFatsLine && (
                        <LinePath
                          data={data}
                          x={(d) => xScale(x(d))}
                          y={(d) => yScale(mediumChainFats(d) || 0)}
                          stroke={props.theme?.darkGreen}
                          strokeWidth={strokeWidth}
                          curve={curveLinear}
                        />
                      )}
                      {/* circles are the last elements to render so the tooltip can show properly */}
                      {showCarbohydratesLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`carbohydrates-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(carbohydrates(point))}
                              stroke={props.theme?.black}
                              fill={
                                point.completed
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.carbohydrates,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showProteinLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`protein-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(protein(point))}
                              stroke={props.theme?.black}
                              fill={
                                point.completed
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.protein,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showLongChainFatsLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`longChainFats-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(longChainFats(point))}
                              stroke={props.theme?.black}
                              fill={
                                point.completed
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.longChainFats,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                      {showMediumChainFatsLine && (
                        <>
                          {data.map((point, pointIndex) => (
                            <Circle
                              key={pointIndex}
                              data-testid={`mediumChainFats-${pointIndex}`}
                              r={5}
                              cx={xScale(x(point))}
                              cy={yScale(mediumChainFats(point) || 0)}
                              stroke={props.theme?.black}
                              fill={
                                point.completed
                                  ? props.theme?.black
                                  : props.theme?.white
                              }
                              onMouseMove={(e) =>
                                handleShowTooltip(
                                  e,
                                  point.mediumChainFats,
                                  point.meals.length
                                )
                              }
                              onMouseOut={() => hideTooltip()}
                            />
                          ))}
                        </>
                      )}
                    </Group>
                  </svg>
                  {tooltipOpen && (
                    <Tooltip
                      key={Math.random()}
                      top={tooltipTop}
                      left={tooltipLeft}
                      style={tooltipStyles}
                    >
                      <div>
                        <strong>
                          {`${
                            tooltipData?.data?.percentage
                              ? `${roundMealPercentage(
                                  roundMealValue(tooltipData.data.percentage)
                                )}${UNIT.PERCENTAGE} - `
                              : ""
                          }${roundMealValue(tooltipData?.data?.value || 0)}${
                            tooltipData?.data?.unit || ""
                          }`}
                        </strong>
                      </div>
                      <div>
                        <strong>{`${t("appText.dietDiary.entries")} ${
                          tooltipData.nMeals
                        }`}</strong>
                      </div>
                    </Tooltip>
                  )}
                </StyledWrapper>
              </LineChartWrapper>
            </>
          );
        }}
      </ParentSize>
      <LegendWrapper>
        <span className="description">
          {t("appText.dietDiary.macronutrientsChart.help")}
        </span>
        <div className="legend">
          <LegendOrdinal
            scale={ordinalColorScale}
            labelFormat={(label) =>
              `${label.charAt(0).toUpperCase()}${label.slice(1)}`
            }
          >
            {(labels) => (
              <LabelsComponent>
                {labels.map((label, i) => (
                  <LegendItem
                    data-testid={`legend-item-${label.datum}`}
                    key={`legend-quantile-${i}`}
                    margin="0 25px 0 0"
                    onClick={() => handleClickLegend(label)}
                  >
                    <svg width={20} height={20} style={{ cursor: "pointer" }}>
                      <g>
                        <rect fill={label.value} width={20} height={20} />
                        {legendList?.includes(label.datum) && (
                          <svg width="20" height="13" viewBox="0 0 20 20">
                            <path
                              fill={props.theme?.white}
                              d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"
                            />
                          </svg>
                        )}
                      </g>
                    </svg>
                    <StyledLegendLabel align="left" margin="0 0 0 4px">
                      {getLegendName(label)}
                    </StyledLegendLabel>
                  </LegendItem>
                ))}
              </LabelsComponent>
            )}
          </LegendOrdinal>
          <LegendInfo>
            <div className="me-3">
              <StyledCircle height="12" width="12">
                <Circle
                  cx="6"
                  cy="6"
                  r={5}
                  stroke={props.theme?.black}
                  fill={props.theme?.black}
                />
              </StyledCircle>
              <span>
                {t("appText.dietDiary.macronutrientsChart.completeDays")}
              </span>
            </div>
            <div>
              <StyledCircle height="12" width="12">
                <Circle
                  cx="6"
                  cy="6"
                  r={5}
                  stroke={props.theme?.black}
                  fill={props.theme?.white}
                />
              </StyledCircle>
              <span>
                {t("appText.dietDiary.macronutrientsChart.notCompleteDays")}
              </span>
            </div>
          </LegendInfo>
        </div>
      </LegendWrapper>
    </>
  );
};

export default withTheme(withTooltip(StudyLineChart));
